"use client";

import { useMemo } from "react";
import OpeningStatus from "./opening-status";
import {
  getOpeningForDay,
  isWithinOpeningPeriods,
} from "@/app/utils/helpers/format/opening-hours";
import type {
  OpeningHourT,
  SpecialOpeningHourT,
} from "@/app/types/opening-hour";

const TIMEZONE = "Europe/Berlin";

interface OpeningStatusClientProps {
  opening_hours: OpeningHourT[];
  special_opening_hours: SpecialOpeningHourT[];
}

/**
 * Returns current date/time in Europe/Berlin timezone.
 * Uses Intl for reliable timezone handling across environments.
 */
function getBerlinNow(): Date {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const part = (key: string) => parts.find((p) => p.type === key)?.value ?? "0";
  const year = parseInt(part("year"), 10);
  const month = parseInt(part("month"), 10) - 1;
  const day = parseInt(part("day"), 10);
  const hour = parseInt(part("hour"), 10);
  const minute = parseInt(part("minute"), 10);
  const second = parseInt(part("second"), 10);
  return new Date(year, month, day, hour, minute, second);
}

export default function OpeningStatusClient({
  opening_hours,
  special_opening_hours,
}: OpeningStatusClientProps) {
  const isOpen = useMemo(() => {
    const berlinNow = getBerlinNow();
    const berlinWeekday = berlinNow.getDay() === 0 ? 7 : berlinNow.getDay();
    const openingNow = getOpeningForDay(
      berlinWeekday,
      opening_hours,
      special_opening_hours,
      berlinNow
    );
    if (!openingNow || openingNow.is_closed || !openingNow.periods?.length) {
      return false;
    }
    const validPeriods = openingNow.periods.reduce<
      Array<{ opens: string; closes: string }>
    >((acc, p) => {
      if (p.opens && p.closes) acc.push({ opens: p.opens, closes: p.closes });
      return acc;
    }, []);
    return isWithinOpeningPeriods(validPeriods, berlinNow);
  }, [opening_hours, special_opening_hours]);

  return <OpeningStatus isOpen={isOpen} />;
}
